import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { FileText, Clock, IndianRupee, Package, Layers, Send, Check, Loader2, ArrowRight } from "lucide-react";
import { formatGeneratedAt } from "../lib/datetime";

const STATUS_COLOR = {
  draft: "border-bm-dim/40 text-bm-dim",
  in_review: "border-bm-cyan/40 text-bm-cyan",
  issued_to_supplier: "border-bm-amber/40 text-bm-amber",
  in_development: "border-bm-green/40 text-bm-green",
  approved: "border-bm-green/60 text-bm-green",
  killed: "border-bm-danger/40 text-bm-danger",
};

function ChipList({ items, color }) {
  if (!items?.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((x) => (
        <span key={x} className={`text-[11px] font-mono px-1.5 py-0.5 border ${color}`}>{x}</span>
      ))}
    </div>
  );
}

function BriefCard({ b, idx }) {
  const navigate = useNavigate();
  const statusCls = STATUS_COLOR[b.status] || "border-white/15 text-bm-muted";
  const [issuing, setIssuing] = useState(false);
  const [result, setResult] = useState(null);

  const issueRfp = async () => {
    if (issuing) return;
    setIssuing(true);
    try {
      const r = await api.issueRfpFromBrief(b.id);
      setResult(r);
    } finally {
      setIssuing(false);
    }
  };
  return (
    <div
      data-testid={`brief-card-${b.id}`}
      className="bm-card fade-up p-5 flex flex-col gap-3"
      style={{ animationDelay: `${Math.min(idx * 50, 500)}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="label-mono text-bm-cyan">{b.category}</span>
          {b.subcategory && <span className="label-mono text-bm-amber">· {b.subcategory}</span>}
        </div>
        <span className={`label-mono px-1.5 py-0.5 border ${statusCls}`} data-testid={`brief-status-${b.id}`}>
          {b.status.replaceAll("_", " ").toUpperCase()}
        </span>
      </div>

      <h3 className="font-heading text-lg font-black leading-tight text-white" data-testid={`brief-title-${b.id}`}>
        {b.title}
      </h3>

      <p className="text-sm text-bm-text/90 leading-relaxed">{b.objective}</p>

      <div className="border-t border-white/5 pt-3">
        <div className="label-mono text-bm-dim mb-1.5">TARGET CONSUMER</div>
        <div className="text-xs text-bm-muted leading-relaxed">{b.target_consumer}</div>
      </div>

      {b.must_have_claims?.length > 0 && (
        <div>
          <div className="label-mono text-bm-dim mb-1.5">MUST-HAVE CLAIMS</div>
          <ChipList items={b.must_have_claims} color="border-bm-green/40 text-bm-green" />
        </div>
      )}

      {b.nice_to_have_claims?.length > 0 && (
        <div>
          <div className="label-mono text-bm-dim mb-1.5">NICE-TO-HAVE</div>
          <ChipList items={b.nice_to_have_claims} color="border-bm-cyan/30 text-bm-cyan" />
        </div>
      )}

      {b.ingredient_guardrails?.length > 0 && (
        <div>
          <div className="label-mono text-bm-dim mb-1.5">INGREDIENT GUARDRAILS</div>
          <ChipList items={b.ingredient_guardrails} color="border-bm-danger/30 text-bm-danger" />
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border-t border-white/5 pt-3">
        <div>
          <div className="label-mono text-bm-dim flex items-center gap-1"><IndianRupee className="w-3 h-3" />TARGET</div>
          <div className="font-mono text-sm text-white">{b.target_price_inr || "—"}</div>
        </div>
        <div>
          <div className="label-mono text-bm-dim">MARGIN</div>
          <div className="font-mono text-sm text-bm-green">{b.target_margin_pct || "—"}%</div>
        </div>
        <div>
          <div className="label-mono text-bm-dim flex items-center gap-1"><Package className="w-3 h-3" />PACK</div>
          <div className="font-mono text-xs text-white">{b.pack_size || "—"}</div>
        </div>
        <div>
          <div className="label-mono text-bm-dim flex items-center gap-1"><Layers className="w-3 h-3" />MOQ</div>
          <div className="font-mono text-sm text-white">{b.moq_units ? `${(b.moq_units/1000).toFixed(0)}k` : "—"}</div>
        </div>
      </div>

      {b.sensory_targets?.length > 0 && (
        <div className="border-t border-white/5 pt-3">
          <div className="label-mono text-bm-dim mb-1.5">SENSORY TARGETS</div>
          <ul className="space-y-0.5 text-xs text-bm-muted">
            {b.sensory_targets.map((s) => (
              <li key={s} className="flex gap-2"><span className="text-bm-amber shrink-0">→</span>{s}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-between text-[10px] font-mono pt-2 border-t border-white/5">
        <span className="text-bm-dim">LAUNCH · {b.target_launch_window || "—"}</span>
        <div className="flex items-center gap-1 text-bm-dim">
          <Clock className="w-3 h-3" />{formatGeneratedAt(b.generated_at || b.created_at)}
        </div>
      </div>

      {/* Issue RFP block */}
      {result ? (
        <div
          data-testid={`rfp-issued-${b.id}`}
          className="border-l-2 border-bm-green pl-3 py-2 bg-bm-green/5 flex items-center justify-between gap-2"
        >
          <div className="flex items-center gap-2 text-xs font-mono">
            <Check className="w-3.5 h-3.5 text-bm-green shrink-0" />
            <span className="text-bm-green">
              {result.already_issued ? "RFP already exists" : `Draft RFP created · ${result.shortlist_size} suppliers shortlisted`}
            </span>
          </div>
          <button
            onClick={() => navigate(`/rfps?focus=${result.rfp_id}`)}
            data-testid={`open-rfp-${b.id}`}
            className="flex items-center gap-1 px-2 py-1 text-[11px] font-mono bg-bm-green text-black hover:bg-white"
          >
            OPEN RFP <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button
          onClick={issueRfp}
          disabled={issuing || b.status === "killed"}
          data-testid={`issue-rfp-btn-${b.id}`}
          className="w-full flex items-center justify-center gap-2 mt-1 px-3 h-9 text-xs font-mono bg-bm-amber text-black hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {issuing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {issuing ? "DRAFTING RFP…" : "ISSUE RFP"}
        </button>
      )}
    </div>
  );
}

export default function Briefs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    api.briefs(statusFilter ? { status: statusFilter } : {}).then((d) => {
      setItems(d.items || []);
      setLoading(false);
    });
  }, [statusFilter]);

  const statuses = ["draft", "in_review", "issued_to_supplier", "in_development", "approved", "killed"];

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-bm-amber flex items-center gap-2">
            <FileText className="w-3.5 h-3.5" />VENDOR-FACING PRODUCT BRIEFS
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
            What we want built.
          </h1>
          <p className="text-sm text-bm-muted mt-2 max-w-3xl">
            Manufacturer-ready briefs — claims, ingredient guardrails, pack format, sensory targets, MOQ, target price.
            One step downstream of Opportunities. One step upstream of RFPs.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="brief-status-filters">
          <button
            onClick={() => setStatusFilter("")}
            data-testid="brief-filter-all"
            className={`px-2.5 py-1 text-xs font-mono border transition-colors ${
              !statusFilter ? "bg-bm-amber text-black border-bm-amber" : "text-bm-muted border-white/15 hover:border-bm-amber/50 hover:text-white"
            }`}
          >
            ALL
          </button>
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`brief-filter-${s}`}
              className={`px-2.5 py-1 text-xs font-mono border transition-colors ${
                statusFilter === s ? "bg-bm-amber text-black border-bm-amber" : "text-bm-muted border-white/15 hover:border-bm-amber/50 hover:text-white"
              }`}
            >
              {s.replaceAll("_", " ").toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="font-mono text-xs text-bm-cyan scanline p-6">LOADING BRIEFS...</div>}

      {!loading && items.length === 0 && (
        <div className="text-center py-12 font-mono text-sm text-bm-muted">No briefs in this status.</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="brief-grid">
        {items.map((b, i) => <BriefCard key={b.id} b={b} idx={i} />)}
      </div>
    </div>
  );
}
